/* eslint-disable */
import * as Sentry from "@sentry/react";
import axios from "axios";
import { initReactQueryAuth } from "react-query-auth";
import { isString } from "lodash";

import Loader from "../components/Loader.jsx";
import { isWpAdmin, logoutUser, setLocalCredentials } from "../helpers/authHelpers.js";
import { graphql } from "./wp.api.service.js";
import { graphqlDate } from "../helpers/dates.js";
import { subDays } from "date-fns";

/**
 * User Fragment
 *
 * GraphQL User Fragment to reuse in other calls
 */
const userFragment = `
	fragment UserFragment on User {
		databaseId
		email
		emailVerified
		name
		firstName
		lastName
		roles {
			nodes {
				name
			}
		}
		attendeeDetails {
			aboutYourself
			addressLine1
			addressLine2
			cellPhone
			city
			emergencyName
			emergencyPhone
			emergencyRelationship
			howDidYouFindUs
			interest
			profileCompleted
			topics
			state
			dateOfBirth
			zipCode
		}
		attendeeDemographics {
			ethnicGroup
			race
			sexLegal
			sexBirth
			genderIdentity
			pronouns
			pronounsOther
			demographicsCompleted
			sexualOrientation
		}
		accountStatus {
			termsAgreedTs
		}
		jwtRefreshToken
		jwtAuthToken
		jwtAuthExpiration,
		attendees(first: 1) {
			edges {
				node {
					id: databaseId
				}
			}
		}
		facilitators(first: 1) {
			edges {
				node {
					id: databaseId
					facilitatorDetails {
						additionalCapabilities
					}
				}
			}
		}
		registrations(first: 1000, where: {dateQuery: {after: $after}}) {
			edges {
			  node {
				  id: databaseId
				  course {
					  node {
						  courseId
						  title
					  }
				  }
			  }
			}
		}
	}
`;

/**
 * Transform Returned User Object
 *
 * Transforms the graphql output to the shape we want to consume it in.
 *
 * @param {object} user The user
 */
const transformUser = (user) => ({
	...user,
	attendeeId: user.attendees?.edges[0]?.node.id,
	facilitatorId: user.facilitators?.edges[0]?.node.id,
	additionalCapabilities: user.facilitators?.edges[0]?.node.facilitatorDetails.additionalCapabilities,
	registrations: user.registrations?.edges?.reduce((prev, { node }) => {
		if (node?.course?.node?.courseId) {
			prev[node.course.node.courseId] = {
				id: node.id,
				courseId: node.course.node.courseId,
				courseTitle: node.course.node.title
			};
		}
		return prev;
	}, {}),
	roles: user.roles.nodes.map((role) => role.name)
});

/**
 * The API Base
 */
const API_URL = process.env.REACT_APP_API_URL || ""; /* eslint-disable-line no-undef */

/**
 * Generate Auth Header
 *
 * Generates the auth headers necessary for an API call to WordPress.
 * First detects to see if the wpApiSettings variable is set and then
 * falls back to using JWT.
 *
 * @returns object
 */
export const authHeader = () => {
	const user = JSON.parse(localStorage.getItem("user"));

	const headers = {};

	const { wpApiSettings } = window;
	if (wpApiSettings && wpApiSettings.nonce) {
		headers["X-WP-Nonce"] = wpApiSettings.nonce;
	} else if (user && user.token) {
		headers.Authorization = `Bearer ${user.token}`;
	}

	return headers;
};

/**
 * WordPress JWT Login Function
 *
 * Requests a new JWT token and then gets the user data.
 *
 * @param {email, password} Credentials The login credentials.
 * @returns Promise
 */
const loginFn = ({ email, magic, password }) => {
	const loginUrl = `${API_URL}/graphql`;

	let query = `
		${userFragment}
		mutation LoginUser($username: String!, $password: String!, $after: DateInput = {}) {
			login(
				input: {
					clientMutationId: "uniqueId",
					username: $username,
					password: $password
				}
			) {
				authToken
				refreshToken
				user {
					...UserFragment
				}
			}
		}
	`;

	if ( magic ) {
		query = `
		${userFragment}
		mutation DoMagicLogin($password: String!, $username: String!, $after: DateInput = {}) {
			doMagicLogin(input: {key: $password, username: $username}) {
			  authToken
			  refreshToken
			  user {
				  ...UserFragment
			  }
			}
		}
		`
	}

	return axios.
		post(loginUrl, {
			query,
			variables: {
				password,
				username: email,
				after: graphqlDate(subDays(new Date(), 1))
			}
		}).
		then((res) => {
			if (res.status === 200) {
				if (res.data.errors) {
					throw new Error(res.data.errors[0].message);
				}

				const loginData = res.data?.data?.login || res.data?.data?.doMagicLogin;
				if (loginData) {
					const { user, authToken, refreshToken } = loginData;
					const [userRole] = user.roles.nodes;

					const adminRoles = ["administrator", "staff", "facilitator"];
					if (
						adminRoles.indexOf( userRole?.name ) !== -1
						&& window.location.host.search("localhost") === -1
					) {
						window.location.href = window.location.protocol + "//" + window.location.host + "/";
					} else {
						setLocalCredentials(user.email, authToken, refreshToken, user.jwtAuthExpiration);
						return transformUser(user);
					}
				} else {

					localStorage.removeItem("user");
					localStorage.removeItem("jwtAuthExpiration");
					throw new Error("Bad login credentials.");

				}
			} else {
				localStorage.removeItem("user");
				localStorage.removeItem("jwtAuthExpiration");
				throw new Error("Login error.");
			}
		}).
		catch((res) => {
			console.log(res);
			localStorage.removeItem("user");
			throw new Error(res.message);
		});
};

/**
 * WordPress JWT Logout Function
 *
 * Logs the user out.
 *
 * @todo Log user out of WordPress.
 *
 * @returns Promise
 */
const logoutFn = () => new Promise((resolve) => {
	logoutUser();

	if (window.wpApiSettings) {
		window.location.href = `/wp-login.php?action=logout&_wpnonce=${window.wpApiSettings.logoutNonce}`;
	}

	resolve(false);
});

/**
 * WordPress Load User Function
 *
 * Used to get the latest user data. This function is used by
 * React Query to get the latest server user state.
 *
 * @returns Promise
 */
const loadUser = () => {

	let user = window.localStorage.getItem("user") || null;

	if ( window.wpApiSettings ) {
		user = window.wpApiSettings;
	}

	if (user) {
		user = isString(user) ? JSON.parse(user) : user;
		const query = `
			${userFragment}
			query MyQuery( $id: ID!, $after: DateInput = {} ) {
				user(id: $id, idType: EMAIL) {
					...UserFragment
				}
			}
		`;

		return graphql({
			query,
			variables: {
				id: user.email,
				after: graphqlDate(subDays(new Date(), 1))
			}
		}).
			then((res) => {
				if (res.data?.data?.user) {
					if (!window.wpApiSettings) {
						const {
							email,
							jwtAuthExpiration,
							jwtAuthToken: token,
							jwtRefreshToken: refreshToken,
							roles
						} = res.data.data.user;
						const userAuth = {
							email: email,
							refreshToken,
							role: roles?.nodes[0]?.name || 'attendee',
							token
						};
						localStorage.setItem("user", JSON.stringify(userAuth));
						localStorage.setItem("jwtAuthExpiration", parseInt(jwtAuthExpiration, 10) * 1000);
					}

					return transformUser(res.data.data.user);
				} else {
					localStorage.removeItem("user");
					window.location.href = "/login";
					return;
				}
			}).
			catch((err) => {
				localStorage.removeItem("user");
				window.location.href = "/login";
			});
	}

	return false;
};

/* eslint-disable */
/**
 * WordPress Register Function
 * 
 * @todo Write Registration Function
 * 
 * @returns Promise
 */
const registerFn = async () => new Promise( (resolve, reject) => {
	console.log("registering");
});

/**
 * Request Password Reset Query Function
 * 
 * @param function useMutation The useMutation hook.
 * @param object props Custom mutation properties.
 * @returns Promise
 */
export const requestPasswordResetFn = (useMutation, props) => {
	const query = `
	mutation RequestReset($username: String = "") {
		sendPasswordResetEmail(input: {username: $username}) {
		  clientMutationId
		}
	}
	`;

	return useMutation(
		(username) => graphql({
			query,
			variables: {
				username
			}
		}, true),
		props
	);
};

/**
 * Reset Password Query Function
 * 
 * @param function useMutation The useMutation hook.
 * @param object props The custom mutation props.
 * @returns Promise
 */
export const resetPasswordFn = (useMutation, props) => {
	const query = `
	mutation ResetPassword($key: String = "", $login: String = "", $password: String = "") {
		resetUserPassword(input: {key: $key, login: $login, password: $password}) {
		  user {
			email
		  }
		}
	}
	`;

	return useMutation(
		(variables) => graphql({
			query,
			variables
		}, true),
		props
	);
}

/**
 * Generate Auth Provider
 *
 * Using React Query Auth npm package to generate the AuthProvider
 * and useAuth hook.
 *
 * Any component that needs to reference the current user can get it from the
 * useAuth() hook via destructuring. i.e. const { user } = useAuth()
 *
 * @see https://www.npmjs.com/package/react-query-auth
 */
export const { AuthProvider, useAuth } = initReactQueryAuth({
	LoaderComponent: Loader,
	loadUser,
	loginFn,
	logoutFn,
	registerFn
});

/**
 * Determine If User Should Refresh JWT Token
 * @returns Boolean
 */
export const shouldRevalidateToken = () => {

	if ( isWpAdmin() ) {
		return false;
	}

	const lastLogin = localStorage.getItem("lastLoginTime") ?  parseInt(localStorage.getItem("lastLoginTime"), 10) : false;
	const jwtAuthExpiration = localStorage.getItem("jwtAuthExpiration") ? parseInt(localStorage.getItem("jwtAuthExpiration"), 10) : false;
	const now = new Date().getTime();
	const maxLoginLength = 1000 * 60 * 60 * 24 * 30;

	if (!lastLogin || now - lastLogin >= maxLoginLength) {
		logoutUser();
		return true;
	}

	if (!jwtAuthExpiration || now > jwtAuthExpiration) {
		return true;
	}

	return false;
};

/**
 * Revalidate JWT Auth Token
 *
 * Handles revalidating user token if they have chosen to be
 * kept logged in.
 *
 * @returns Promise
 */
export const revalidateToken = () => {
	if (!shouldRevalidateToken()) {
		return false;
	}

	let user = false;
	try {
		user = JSON.parse(localStorage.getItem("user"));
	} catch (error) {
		return false;
	}

	// eslint-disable-next-line no-console
	console.log("Refetching token");

	const query = `
		mutation MyMutation($jwtRefreshToken: String = "") {
			refreshJwtAuthToken(input: {jwtRefreshToken: $jwtRefreshToken}) {
				authToken
			}
		}
	`;

	return graphql({
		query,
		variables: {
			jwtRefreshToken: user.refreshToken
		}
	}, true).
		then(({ data: { data } }) => {
			if ( data.refreshJwtAuthToken ) {
				user.token = data.refreshJwtAuthToken.authToken;
				localStorage.setItem("user", JSON.stringify(user));
				return true;
			}
			return false;
		});
};
